import { PaintCommand } from '../core/EditorActions.js';

/**
 * Auto-tiling terrain brush using Wang tile matching.
 * Supports corner + edge terrain sets (16-tile or 48-tile blobs).
 *
 * Terrain definition:
 * {
 *   name: 'Grass',
 *   baseTile: 1,          // Default/center tile
 *   tiles: {              // Cardinal + corner auto-tile mappings
 *     center: 1,
 *     north: 2, south: 3, east: 4, west: 5,
 *     ne: 6, nw: 7, se: 8, sw: 9,
 *     northSouth: 10, eastWest: 11,
 *     // ... etc for all 16/48 combinations
 *   },
 *   matchTiles: [1,2,3,...],  // All tiles that count as "this terrain"
 * }
 */
export class TerrainBrush {
  constructor(state, mapCanvas, commandHistory) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.commandHistory = commandHistory;
    this._terrain = null;
    this._changes = [];
    this._painted = new Set();
  }

  setTerrain(terrain) {
    this._terrain = terrain;
  }

  onDown(tx, ty) {
    if (!this._terrain) return;
    this._changes = [];
    this._painted.clear();
    this._paint(tx, ty);
  }

  onMove(tx, ty) {
    if (!this._terrain) return;
    this._paint(tx, ty);
  }

  onUp() {
    if (this._changes.length > 0) {
      const layerIndex = this.state.get('activeLayerIndex');
      const cmd = new PaintCommand(this.state, layerIndex, this._changes);
      this.commandHistory.push(cmd);
    }
    this._changes = [];
    this._painted.clear();
  }

  _paint(tx, ty) {
    const state = this.state;
    const mapW = state.get('mapWidth');
    const mapH = state.get('mapHeight');
    const layerIndex = state.get('activeLayerIndex');

    if (tx < 0 || ty < 0 || tx >= mapW || ty >= mapH) return;
    const key = `${tx},${ty}`;
    if (this._painted.has(key)) return;
    this._painted.add(key);

    // Place the base tile
    const oldTile = state.getTile(layerIndex, tx, ty);
    const baseTile = this._terrain.baseTile || this._terrain.tiles?.center;
    if (baseTile && oldTile !== baseTile) {
      state.setTile(layerIndex, tx, ty, baseTile);
      this._changes.push({ x: tx, y: ty, oldTile, newTile: baseTile });
    }

    // Update this tile and all neighbors
    this._updateAutoTile(layerIndex, tx, ty, mapW, mapH);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        this._updateAutoTile(layerIndex, tx + dx, ty + dy, mapW, mapH);
      }
    }

    this.mapCanvas.invalidateLayer(layerIndex);
  }

  _updateAutoTile(layerIndex, tx, ty, mapW, mapH) {
    if (tx < 0 || ty < 0 || tx >= mapW || ty >= mapH) return;

    const currentTile = this.state.getTile(layerIndex, tx, ty);
    if (!this._isTerrainTile(currentTile)) return;

    // Check 8 neighbors
    const n = this._isTerrainTile(this.state.getTile(layerIndex, tx, ty - 1));
    const s = this._isTerrainTile(this.state.getTile(layerIndex, tx, ty + 1));
    const e = this._isTerrainTile(this.state.getTile(layerIndex, tx + 1, ty));
    const w = this._isTerrainTile(this.state.getTile(layerIndex, tx - 1, ty));
    const ne = this._isTerrainTile(this.state.getTile(layerIndex, tx + 1, ty - 1));
    const nw = this._isTerrainTile(this.state.getTile(layerIndex, tx - 1, ty - 1));
    const se = this._isTerrainTile(this.state.getTile(layerIndex, tx + 1, ty + 1));
    const sw = this._isTerrainTile(this.state.getTile(layerIndex, tx - 1, ty + 1));

    // Compute bitmask (4-bit cardinal for 16-tile sets)
    const mask = (n ? 1 : 0) | (e ? 2 : 0) | (s ? 4 : 0) | (w ? 8 : 0);

    const tiles = this._terrain.tiles;
    if (!tiles) return;

    // Map bitmask to tile
    const tileMap = tiles.autoTileMap || this._buildAutoTileMap(tiles);
    const newTile = tileMap[mask];
    if (newTile === undefined || newTile === currentTile) return;

    const oldTile = currentTile;
    this.state.setTile(layerIndex, tx, ty, newTile);

    // Track for undo only if not already in changes
    const changeKey = `${tx},${ty}`;
    if (!this._painted.has(changeKey)) {
      this._changes.push({ x: tx, y: ty, oldTile, newTile });
    }
  }

  _isTerrainTile(tileId) {
    if (!this._terrain) return false;
    if (this._terrain.matchTiles) return this._terrain.matchTiles.includes(tileId);
    const tiles = this._terrain.tiles;
    if (!tiles) return tileId === this._terrain.baseTile;
    return Object.values(tiles).includes(tileId);
  }

  _buildAutoTileMap(tiles) {
    // Default 16-tile cardinal mapping
    // mask: N=1, E=2, S=4, W=8
    return {
      0:  tiles.isolated || tiles.center,  // No neighbors
      1:  tiles.north || tiles.center,      // N only
      2:  tiles.east || tiles.center,       // E only
      3:  tiles.ne || tiles.center,         // N+E
      4:  tiles.south || tiles.center,      // S only
      5:  tiles.northSouth || tiles.center, // N+S
      6:  tiles.se || tiles.center,         // S+E
      7:  tiles.northSouthEast || tiles.center,
      8:  tiles.west || tiles.center,       // W only
      9:  tiles.nw || tiles.center,         // N+W
      10: tiles.eastWest || tiles.center,   // E+W
      11: tiles.northEastWest || tiles.center,
      12: tiles.sw || tiles.center,         // S+W
      13: tiles.northSouthWest || tiles.center,
      14: tiles.southEastWest || tiles.center,
      15: tiles.center,                     // All neighbors
    };
  }
}
